/**
 * JWT Auth Provider for Expense Admin
 *
 * Manages JWT token lifecycle using the app authClient singleton configured with
 * VITE_AUTH_SERVICE_URL. All token reads/writes go through the same instance used by
 * LoginPage and useOtcExchange, eliminating auth endpoint drift.
 */
import { type ReactNode, createContext, useContext, useEffect, useMemo, useState } from "react";
import type { JWTPayload } from "@its-engineering-and-development/jwt-auth-client";
import { authClient } from "@/api/auth-client";
import { expenseAdminTokenSource } from "@/auth";

// ============================================================================
// TYPES
// ============================================================================

interface JwtAuthContextType {
    user: JWTPayload | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    logout: () => void;
    hasRole: (role: string, domain?: string) => boolean;
    setTokens: (accessToken: string, refreshToken: string) => void;
}

// ============================================================================
// CONTEXT
// ============================================================================

const JwtAuthContext = createContext<JwtAuthContextType | null>(null);

export function useJwtAuth(): JwtAuthContextType {
    const context = useContext(JwtAuthContext);
    if (!context) {
        throw new Error("useJwtAuth must be used within JwtAuthProvider");
    }
    return context;
}

// ============================================================================
// PROVIDER
// ============================================================================

export function JwtAuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<JWTPayload | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Load user from stored token on mount and sync across tabs
    useEffect(() => {
        const loadUser = () => {
            try {
                const decodedToken = authClient.decodeToken();
                if (decodedToken && !authClient.isTokenExpired()) {
                    setUser(decodedToken);
                } else {
                    setUser(null);
                }
            } catch (err) {
                console.warn("Failed to decode token:", err);
                setUser(null);
            }
            setIsLoading(false);
        };

        loadUser();

        // Sync across tabs when tokens change (fires only in *other* tabs).
        window.addEventListener("storage", loadUser);
        // Sync within the current tab: the token source polls the stored token,
        // so login/logout in this tab re-decode without a full reload.
        // We use a wrapper to ensure we only trigger a state update if the token string actually changed,
        // preventing unnecessary re-renders or potential library-triggered reloads.
        const unsubscribe = expenseAdminTokenSource.onTokenChange((newToken) => {
            // Note: authClient.getAccessToken() is used here to get the raw string for comparison
            // to avoid triggering updates if the decoded payload is identical but the object reference changed.
            const currentRawToken = authClient.getAccessToken();
            if (newToken === currentRawToken) {
                loadUser();
            }
        });
        return () => {
            window.removeEventListener("storage", loadUser);
            unsubscribe();
        };
    }, []);

    const logout = () => {
        authClient.clearTokens();
        setUser(null);
    };

    const setTokens = (accessToken: string, refreshToken: string) => {
        authClient.setTokens(accessToken, refreshToken);
        // Decode and update user
        try {
            const decoded = authClient.decodeToken();
            if (decoded) {
                setUser(decoded);
            }
        } catch (err) {
            console.warn("Failed to decode token after setting:", err);
        }
    };

    const hasRole = (role: string, _domain?: string): boolean => {
        const roles = user?.roles as unknown;

        if (!roles) {
            return false;
        }

        if (Array.isArray(roles)) {
            return roles.includes(role);
        }

        if (typeof roles === "object") {
            return Object.values(roles as Record<string, unknown>).some((domainRoles) => Array.isArray(domainRoles) && domainRoles.includes(role));
        }

        return false;
    };

    const value = useMemo<JwtAuthContextType>(
        () => ({
            user,
            isAuthenticated: !!user,
            isLoading,
            logout,
            hasRole,
            setTokens,
        }),
        [user, isLoading, hasRole],
    );

    return <JwtAuthContext.Provider value={value}>{children}</JwtAuthContext.Provider>;
}
